import {inject, Injectable} from '@angular/core';

import {TaskListDto} from '@dto/tasks/task-list.dto';
import {TaskDetailDto} from '@dto/tasks/task-detail.dto';
import {TaskViewModel} from '../view-models/task.view-model';
import {TaskDto} from '@dto/tasks/task.dto';
import {TaskStepTypeEnum} from '@dto/tasks/task-step-type.enum';
import {TaskStepAdaptiveCardViewModel} from '../view-models/task-step-adaptive-card.view-model';
import {TaskStepFileUploadViewModel} from '../view-models/task-step-file-upload-view.model';
import {FileConverterService} from '@shared/services/file-converter.service';
import {TaskStepDetailViewModel} from '../view-models/task-step-detail.view-model';
import {TaskStepDetailDto} from '@dto/tasks/task-step-detail.dto';
import {Validator} from '@shared/utilities/validator';
import {TaskStepAdaptiveCardDto} from '@dto/tasks/task-step-adaptive-card.dto';
import {TaskStepGbgDto} from '@dto/tasks/task-step-gbg.dto';
import {TaskStepFileDownloadDto} from '@dto/tasks/task-step-file-download.dto';
import {TaskStepFileUploadDto} from '@dto/tasks/task-step-file-upload.dto';
import {TaskStepGbgViewModel} from '../view-models/task-step-gbg.view-model';
import {TaskStepFileDownloadViewModel} from '../view-models/task-step-file-download-view.model';
import {APP_TASK} from '../../config/app-urls';
import {TaskListViewModel} from '../view-models/task-list.view-model';
import {TaskDetailViewModel} from '../view-models/task-detail.view-model';
import {TaskStepStatusEnum} from '@dto/tasks/task-step-status.enum';
import {TaskStatusEnum} from '@dto/tasks/task-status.enum';

@Injectable({providedIn: 'root'})
export class TaskConverterService {

  private readonly fileConverterService = inject(FileConverterService);

  public convertTaskDtoToTaskViewModel(task: TaskDto): TaskViewModel {
    return {
      id: task.id,
      readonly: task.status === TaskStatusEnum.Completed,
      description: task.description,
      percentageComplete: task.percentageComplete
    };
  }

  public convertTaskListDtoToTaskListViewModels(taskList: TaskListDto): TaskListViewModel[] {
    return taskList.tasks
      .filter(task => task.status !== TaskStatusEnum.Cancelled)
      .map(task => {
        return this.convertTaskDtoToTaskListViewModel(task);
      });
  }

  private convertTaskDtoToTaskListViewModel(task: TaskDto): TaskListViewModel {
    const viewModel = this.convertTaskDtoToTaskViewModel(task);
    const detailsRouterLinkCommands = APP_TASK.toRouterLinkCommands(viewModel.id);
    const label = this.getTaskButtonLabel(task.status);

    return {
      ...viewModel,
      detailsRouterLinkCommands: detailsRouterLinkCommands,
      detailsLabel: label,
    };
  }

  private getTaskButtonLabel(taskStatus: TaskStatusEnum): string {
    switch (taskStatus) {
      case TaskStatusEnum.New:
        return 'Open';
      case TaskStatusEnum.InProgress:
        return 'Resume';
      case TaskStatusEnum.Completed:
        return 'View';
      case TaskStatusEnum.Cancelled:
        return 'View';
    }
    Validator.verifySwitchHandledAllOptions(taskStatus);
  }

  public convertTaskDetailDtoToTaskDetailViewModel(taskSummary: TaskDto, taskDetail: TaskDetailDto): TaskDetailViewModel {
    const taskStepViewModels = taskDetail.steps.map(step => this.convertTaskStepDetailDtoToTaskStepDetailViewModel(step));
    return this.convertTaskDtoAndStepsToTaskDetailViewModel(taskSummary, taskStepViewModels);
  }

  public convertTaskDtoAndStepsToTaskDetailViewModel(task: TaskDto, stepViewModels: TaskStepDetailViewModel[]): TaskDetailViewModel {
    const taskViewModel = this.convertTaskDtoToTaskViewModel(task);

    return {
      ...taskViewModel,
      steps: stepViewModels
    };
  }

  public convertTaskStepDetailDtoToTaskStepDetailViewModel(step: TaskStepDetailDto): TaskStepDetailViewModel {
    switch (step.type) {
      case TaskStepTypeEnum.AdaptiveCard:
        return this.convertTaskStepAdaptiveCardViewModel(step);
      case TaskStepTypeEnum.GbgIdScan:
        return this.convertTaskStepGbgViewModel(step);
      case TaskStepTypeEnum.FileDownload:
        return this.convertTaskStepFileDownloadViewModel(step);
      case TaskStepTypeEnum.FileUpload:
        return this.convertTaskStepFileUploadViewModel(step);
    }
    Validator.verifySwitchHandledAllOptions(step);
  }

  private convertTaskStepAdaptiveCardViewModel(step: TaskStepAdaptiveCardDto): TaskStepAdaptiveCardViewModel {
    return {
      id: step.id,
      type: TaskStepTypeEnum.AdaptiveCard,
      title: step.title,
      description: step.description,
      completed: step.status === TaskStepStatusEnum.Completed,
      template: step.template,
      data: step.data
    };
  }

  private convertTaskStepGbgViewModel(step: TaskStepGbgDto): TaskStepGbgViewModel {
    return {
      id: step.id,
      type: TaskStepTypeEnum.GbgIdScan,
      title: step.title,
      description: step.description,
      completed: step.status === TaskStepStatusEnum.Completed
    };
  }

  private convertTaskStepFileDownloadViewModel(step: TaskStepFileDownloadDto): TaskStepFileDownloadViewModel {
    const fileDtos = step.files ?? [];
    const fileViewModels = this.fileConverterService.convertFileDtosToFileViewModels(fileDtos);

    return {
      id: step.id,
      type: TaskStepTypeEnum.FileDownload,
      title: step.title,
      description: step.description,
      completed: step.status === TaskStepStatusEnum.Completed,
      files: fileViewModels
    };
  }

  private convertTaskStepFileUploadViewModel(step: TaskStepFileUploadDto): TaskStepFileUploadViewModel {
    const fileDtos = step.files ?? [];
    const fileViewModels = this.fileConverterService.convertFileDtosToFileViewModels(fileDtos);

    return {
      id: step.id,
      type: TaskStepTypeEnum.FileUpload,
      title: step.title,
      description: step.description,
      completed: step.status === TaskStepStatusEnum.Completed,
      files: fileViewModels
    };
  }
}
